import React from "react";
import "./NetworkDiagram.css";

// The two mermaid diagrams from my media-stack architecture note, redrawn as
// on-theme markup instead of embedded images. Sanitized: no internal IPs,
// host names, GPU models, or share paths — same rule as the rest of the site.
// ponytail: primitives duplicated from NetworkDiagram.js (tiny, presentational)
// to keep that working component untouched.
const Node = ({ label, sub, variant }) => (
  <div className={`nd-node ${variant ? `nd-node--${variant}` : ""}`}>
    <span className="nd-node-label">{label}</span>
    {sub && <span className="nd-sub">{sub}</span>}
  </div>
);

const Step = ({ children }) => (
  <span className="nd-step">
    <span className="nd-arrow" aria-hidden="true">→</span>
    {children}
  </span>
);

// The request-to-stream pipeline: two flows layered on the same components.
export const MediaPipelineDiagram = () => (
  <figure className="nd">
    <figcaption className="nd-cap mono">
      {"// media pipeline: request → download → library → stream (sanitized)"}
    </figcaption>

    <div className="nd-zone nd-zone--trusted">
      <span className="nd-zone-label">search &amp; fetch — a request becomes a download</span>
      <div className="nd-flow">
        <Node label="Overseerr" sub="request front-end" />
        <Step>
          <Node label="Radarr / Sonarr" sub="movie / TV automation" variant="accent" />
        </Step>
        <Step>
          <Node label="Prowlarr" sub="indexer manager" variant="accent" />
        </Step>
        <Step>
          <Node label="qBittorrent" sub="download · via ProtonVPN" />
        </Step>
      </div>
      <div className="nd-edge">
        <Node label="FlareSolverr" sub="headless-Chrome CF solver" variant="warn" />
        <span className="nd-note">
          Prowlarr routes Cloudflare-gated indexers through FlareSolverr; plain ones go out direct
        </span>
      </div>
    </div>

    <div className="nd-bridge mono">▼ completed download lands in the library</div>

    <div className="nd-zone nd-zone--trusted">
      <span className="nd-zone-label">import &amp; serve — a download becomes a playable item</span>
      <div className="nd-flow">
        <Node label="qBittorrent" sub="completed file" />
        <Step>
          <Node label="Media library" sub="hardlink + rename into organized tree" variant="accent" />
        </Step>
        <Step>
          <Node label="Plex" sub="scan · transcode · serve" variant="accent" />
        </Step>
        <Step>
          <Node label="Cloudflare Tunnel" sub="sole external ingress" variant="warn" />
        </Step>
        <Step>
          <Node label="Remote clients" sub="tv · phone · browser" />
        </Step>
      </div>
    </div>
  </figure>
);

// Physical/network view: which host runs what, and the three distinct
// traffic paths (two independent egress identities + one inbound tunnel).
const path = { teal: "#0F6E56", coral: "#D85A30", blue: "#378ADD" };

const PathRow = ({ color, name, route }) => (
  <div className="nd-step" style={{ alignItems: "baseline" }}>
    <span
      aria-hidden="true"
      style={{
        width: "0.7rem",
        height: "0.7rem",
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
        display: "inline-block",
      }}
    />
    <span className="nd-node-label" style={{ fontSize: "0.8rem" }}>
      {name}
    </span>
    <span className="nd-note">{route}</span>
  </div>
);

export const MediaNetworkDiagram = () => (
  <figure className="nd">
    <figcaption className="nd-cap mono">{"// media-stack network paths (sanitized)"}</figcaption>

    <div className="nd-zone nd-zone--public">
      <span className="nd-zone-label">untrusted — internet / WAN</span>
      <div className="nd-flow">
        <Node label="ProtonVPN exit" sub="→ torrent swarm" />
        <Node label="Indexer sites" sub="trackers · CF-gated" />
        <Node label="Cloudflare edge" sub="remote client ingress" variant="warn" />
      </div>
    </div>

    <div className="nd-bridge mono">▲ no inbound ports — VPN egress + outbound tunnel only</div>

    <div className="nd-zone nd-zone--trusted">
      <span className="nd-zone-label">trusted — LAN · split-horizon DNS</span>
      <div className="nd-flow">
        <div className="nd-group">
          <span className="nd-group-label">media host — Unraid + GPU</span>
          <div className="nd-chips">
            <span className="nd-chip">arr apps · FlareSolverr</span>
            <span className="nd-chip">qBittorrent · VPN egress</span>
            <span className="nd-chip">Plex</span>
          </div>
        </div>
        <div className="nd-group">
          <span className="nd-group-label">edge Pi</span>
          <div className="nd-chips">
            <span className="nd-chip">cloudflared</span>
            <span className="nd-chip">reverse proxy</span>
            <span className="nd-chip">split DNS</span>
          </div>
        </div>
        <Node label="Spare hypervisor" sub="planned Plex target" />
      </div>
    </div>

    <figcaption className="nd-cap mono" style={{ marginTop: "0.4rem" }}>
      {"// three paths, three separate IPs — a ban on one never touches the others"}
    </figcaption>
    <PathRow
      color={path.teal}
      name="VPN egress"
      route="qBittorrent → ProtonVPN exit → torrent swarm"
    />
    <PathRow
      color={path.coral}
      name="WAN egress"
      route="Prowlarr / FlareSolverr → WAN gateway → indexer sites"
    />
    <PathRow
      color={path.blue}
      name="Cloudflare inbound"
      route="edge → cloudflared → reverse proxy → Plex"
    />
  </figure>
);
